import { Button, Carousel, Tooltip } from 'flowbite-react';
import { useEffect, useState } from 'react';
import Loader from '../Common/Loader';
import blankImage from '../../Assets/NoPhoto.jpg';
import { Link, useParams } from 'react-router-dom';
import { useAppSelector } from '../../Reducers/store';
import { HiEye, HiPencilAlt } from 'react-icons/hi';
import { Equipment } from '../../Models/Equipment';
import EquipmentApi from '../../Api/EquipmentApi';
import { formatNumbers, renderHTML } from '../../Utils';
import {
  EquipmentTypeEnums,
  EquipmentTypeStringEnums,
} from '../../Models/EquipmentTypeEnums';
import { FileTypeEnums } from '../../Models/FileType';

export default function EquipmentProfile({
  equipmentType,
}: {
  equipmentType: EquipmentTypeEnums;
}) {
  const { userModel } = useAppSelector((state) => state.user);
  const { id } = useParams();
  const [equipmentData, setEquipmentData] = useState<Equipment | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getEquipment = () => {
    if (id) {
      setIsLoading(true);
      EquipmentApi.getEquipment(id)
        .then((res) => {
          setEquipmentData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
        });
    } else {
      setEquipmentData(null);
    }
  };

  useEffect(() => {
    getEquipment();
  }, [id]);

  function checkAccess() {
    if (equipmentData && userModel && id) {
      return true;
    }
    return false;
  }

  return (
    <div className='w-full'>
      <div className='w-full p-4'>
        <div className='mx-1 flex items-end'>
          <div className='flex flex-1'>
            <h1 className='text-gray-900 font-bold text-4xl'>
              {EquipmentTypeStringEnums[equipmentType]} Information
            </h1>
          </div>
          {checkAccess() && (
            <div className='right-2'>
              <Link
                to={`/edit/${EquipmentTypeStringEnums[equipmentType]}/${equipmentData?.id}`}
              >
                <Tooltip content='Edit'>
                  <Button className='buttonMain'>
                    <HiPencilAlt size={20} />
                  </Button>
                </Tooltip>
              </Link>
            </div>
          )}
        </div>
      </div>
      <div>
        <div className='container mx-auto p-2'>
          <div className='md:flex no-wrap md:-mx-2 '>
            <div className='w-full md:w-3/12 md:mx-2'>
              <div className='h-56 sm:h-64 xl:h-80 2xl:h-96 overflow-hidden rounded-md'>
                {equipmentData?.files?.filter(
                  (x) => x.fileTypeId === FileTypeEnums.Photo
                ).length === 0 && <img src={blankImage} alt={'blank-image'} />}
                {equipmentData?.files &&
                  equipmentData?.files?.filter(
                    (x) => x.fileTypeId === FileTypeEnums.Photo
                  ).length > 0 && (
                    <Carousel slide={false} className='border-2 rounded-lg'>
                      {equipmentData?.files
                        .filter((x) => x.fileTypeId === FileTypeEnums.Photo)
                        .sort(
                          (a, b) => Number(b.isPrimary) - Number(a.isPrimary)
                        )
                        .map((data) => (
                          <img
                            key={data.id}
                            src={data.fileUrl!}
                            alt={equipmentData.number + '-image'}
                          />
                        ))}
                    </Carousel>
                  )}
              </div>
            </div>
            <div className='w-full md:w-10/12 mx-2'>
              <div className='bg-white p-3'>
                <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8 py-2'>
                  <span className='tracking-wide uppercase text-3xl'>
                    {equipmentData?.manufacturer?.name}
                  </span>
                  {equipmentData?.manufacturerId && (
                    <Link
                      to={`/manufacturer/${equipmentData?.manufacturerId}`}
                      className='buttonMain p-2'
                    >
                      <Tooltip content='View Manufacturer'>
                        <HiEye />
                      </Tooltip>
                    </Link>
                  )}
                </div>
                <hr className='bg-gray-300 h-[5px]'></hr>
                <div className='text-gray-500'>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Categories:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      {equipmentData?.categoriesString &&
                        equipmentData?.categoriesString.map((x) => (
                          <div>{x}</div>
                        ))}
                    </div>
                  </div>
                  <hr className='bg-gray-300 h-[2px]'></hr>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Rimss Id:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      {equipmentData?.rimssId}
                    </div>
                  </div>
                  <hr className='bg-gray-300 h-[2px]'></hr>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Number:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      {equipmentData?.number}
                    </div>
                  </div>
                  <hr className='bg-gray-300 h-[2px]'></hr>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Price:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      $ {formatNumbers(equipmentData?.price)}
                    </div>
                  </div>
                  <hr className='bg-gray-300 h-[2px]'></hr>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Rimss Description:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      {equipmentData?.description}
                    </div>
                  </div>
                  <hr className='bg-gray-300 h-[2px]'></hr>
                  <div className='grid grid-cols-[40%_60%] md:grid-cols-[25%_75%] xl:grid-cols-[14%_86%] py-2 text-xl'>
                    <div className='flex'>
                      <div className='font-bold'>Full Description:</div>
                    </div>
                    <div className='pl-1 text-black'>
                      {renderHTML(equipmentData?.fullDescription ?? '')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
